import { Dispatch, SetStateAction, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import {
  WhitelistedVehicleDTO,
  WhitelistResponseDTO,
  WhitelistSortType,
} from '../../apis/whitelist/dto'
import {
  MoovyGrid,
  MoovyGridCard,
  MoovySearch,
  MoovySortControl,
  MoovyTable,
  MoovyTableControlLayout,
} from '../../components'
import {
  MoovyPaginationProps,
  PaginationAndSortingValues,
} from '../../components/moovyPagination'

import Divider from '@mui/material/Divider'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { useQueryClient, UseQueryResult } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { hasPermission } from '../../app/permissions'
import { StateControl } from '../../components/moovySearch'
import { getSortItems } from '../../components/moovySortControl/sortValues'
import useAuth from '../../hooks/useAuth'
import { prettyFullTimestamp } from '../../utils/datetimeUtils'
import { WhitelistSearchParameters } from './whitelist'

interface WhitelistTableProps {
  whitelist: WhitelistedVehicleDTO[]
  queryWhitelist: UseQueryResult<AxiosResponse<WhitelistResponseDTO>, unknown>
  queryKey: any[]
  onClickRow: (vehicle: WhitelistedVehicleDTO) => void
  defaultSortCriteria: WhitelistSortType
  paginationAndSorting: PaginationAndSortingValues
  setPaginationAndSorting: Dispatch<SetStateAction<PaginationAndSortingValues>>
  searchParametersControl: StateControl<WhitelistSearchParameters>
}

const WhitelistTable = (props: WhitelistTableProps) => {
  const intl = useIntl()
  const { isAdmin } = useAuth()
  const hasEditPermission = hasPermission(isAdmin, 'EDIT', 'WHITELIST')
  const {
    defaultSortCriteria,
    paginationAndSorting,
    setPaginationAndSorting,
    whitelist,
    queryWhitelist,
    queryKey,
    onClickRow,
    searchParametersControl,
  } = props
  const [searchParameters, setSearchParameters] = searchParametersControl
  const [searchInput, setSearchInput] = useState(searchParameters.search)
  const searchFieldControl: StateControl<string> = [searchInput, setSearchInput]
  const queryClient = useQueryClient()
  const onSearch = () => {
    setPaginationAndSorting({
      ...paginationAndSorting,
      page: 1,
    })
    setSearchParameters({
      search: searchInput,
    })
    // Override cache if search value has not changed
    if (searchInput === searchParameters.search) {
      queryClient.invalidateQueries({ queryKey })
    }
  }

  const pagination: MoovyPaginationProps = {
    page: paginationAndSorting.page,
    limit: paginationAndSorting.limit,
    total: queryWhitelist.data?.data.total ?? 0,
    onNextPage: (page: number) => {
      setPaginationAndSorting({
        ...paginationAndSorting,
        page,
      })
    },
  }

  return (
    <>
      <Divider
        sx={{
          marginY: 2,
        }}
      />
      <MoovyTableControlLayout
        searchControl={
          <MoovySearch.SearchBar onSearch={onSearch}>
            <MoovySearch.TextSearchField
              stateControl={searchFieldControl}
              fullWidth
            />
          </MoovySearch.SearchBar>
        }
        sortControl={
          <MoovySortControl
            sortOptions={getSortItems(intl, 'WHITELIST')}
            defaultSortCriteria={defaultSortCriteria}
            paginationAndSortingValues={paginationAndSorting}
            setPaginationAndSortingValues={setPaginationAndSorting}
          />
        }
      />
      <MoovyGrid
        emptyTextId="page.whitelist.table.emptyText"
        errorTextId="common.actions.get.error"
        query={queryWhitelist}
        pagination={pagination}
      >
        {whitelist.map((vehicle) => (
          <MoovyGridCard
            key={vehicle.id}
            cardTitle={
              <Typography fontWeight={600}>{vehicle.licensePlate}</Typography>
            }
            cardSecondaryTitle={<Typography>{vehicle.name}</Typography>}
            cardContentList={[
              <Typography>
                <FormattedMessage id="page.whitelist.table.header.createdAt" />:{' '}
                {prettyFullTimestamp(intl, vehicle.createdAt)}
              </Typography>,
            ]}
            onCardClicked={() => onClickRow(vehicle)}
          />
        ))}
      </MoovyGrid>
      <MoovyTable
        items={whitelist}
        query={queryWhitelist}
        pagination={pagination}
        emptyTextId="page.whitelist.table.emptyText"
        errorTextId="common.actions.get.error"
      >
        <TableHead>
          <TableRow>
            <TableCell>
              <FormattedMessage id="common.text.licensePlate" />
            </TableCell>
            <TableCell>
              <FormattedMessage id="page.whitelist.table.header.name" />
            </TableCell>
            <TableCell align="right">
              <FormattedMessage id="page.whitelist.table.header.createdAt" />
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {whitelist.map((vehicle) => (
            <TableRow
              key={vehicle.id}
              onClick={() => onClickRow(vehicle)}
              hover
              sx={{
                cursor: hasEditPermission ? 'pointer' : 'default',
              }}
            >
              <TableCell>
                <Typography>{vehicle.licensePlate}</Typography>
              </TableCell>
              <TableCell>
                <Typography>{vehicle.name}</Typography>
              </TableCell>
              <TableCell align="right">
                <Typography>
                  {prettyFullTimestamp(intl, vehicle.createdAt)}
                </Typography>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </MoovyTable>
    </>
  )
}

export default WhitelistTable
